/**
 * @fileoverview Image preloader -- warms the room art before Club Penguin draws a scene.
 */

/**
 * Progress callback type
 */
type ProgressCallback = (progress: number, loadedCount: number, totalCount: number) => void;

/**
 * Complete callback type
 */
type CompleteCallback = (loadedImages: HTMLImageElement[]) => void;

/**
 * Error callback type
 */
type ErrorCallback = (error: Error) => void;

/**
 * Image preload error interface
 */
interface ImagePreloadError {
  path: string;
  error: Error;
}

/**
 * Loads a list of images and reports progress as they arrive.
 */
export class ImagePreloader {
  private imageList: string[];
  private loadedImages: HTMLImageElement[];
  private loadedCount: number;
  private totalCount: number;
  private onProgressCallbacks: ProgressCallback[];
  private onCompleteCallbacks: CompleteCallback[];
  private onErrorCallbacks: ErrorCallback[];

  constructor(imageList: readonly string[]) {
    this.imageList = [...imageList];
    this.loadedImages = [];
    this.loadedCount = 0;
    this.totalCount = this.imageList.length;
    this.onProgressCallbacks = [];
    this.onCompleteCallbacks = [];
    this.onErrorCallbacks = [];
  }

  /**
   * Start preloading all images
   */
  preload(): Promise<HTMLImageElement[]> {
    return new Promise((resolve, reject) => {
      if (this.imageList.length === 0) {
        resolve([]);
        return;
      }

      let hasError = false;
      const errors: ImagePreloadError[] = [];

      this.imageList.forEach((imagePath, index) => {
        this.preloadSingleImage(imagePath)
          .then(img => {
            this.loadedImages[index] = img;
            this.loadedCount++;
            this.notifyProgress();

            if (this.loadedCount === this.totalCount) {
              if (hasError) {
                reject(errors);
              } else {
                this.notifyComplete();
                resolve(this.loadedImages);
              }
            }
          })
          .catch(error => {
            hasError = true;
            errors.push({ path: imagePath, error });
            this.loadedCount++;
            this.notifyError(error);

            if (this.loadedCount === this.totalCount) {
              reject(errors);
            }
          });
      });
    });
  }

  /**
   * Preload a single image
   */
  private preloadSingleImage(imagePath: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        resolve(img);
      };

      img.onerror = () => {
        reject(new Error(`Failed to load image: ${imagePath}`));
      };

      // Set source last to avoid race conditions
      img.src = imagePath;
    });
  }

  /**
   * Add image to preload list
   */
  addImage(imagePath: string): void {
    if (!this.imageList.includes(imagePath)) {
      this.imageList.push(imagePath);
      this.totalCount = this.imageList.length;
    }
  }

  /**
   * Add multiple images to preload list
   */
  addImages(imagePaths: string[]): void {
    imagePaths.forEach(path => this.addImage(path));
  }

  /**
   * Remove image from preload list
   */
  removeImage(imagePath: string): void {
    const index = this.imageList.indexOf(imagePath);
    if (index > -1) {
      this.imageList.splice(index, 1);
      this.totalCount = this.imageList.length;
    }
  }

  /**
   * Get loading progress as percentage
   */
  getProgress(): number {
    return this.totalCount === 0 ? 100 : Math.round((this.loadedCount / this.totalCount) * 100);
  }

  /**
   * Check if all images are loaded
   */
  isComplete(): boolean {
    return this.loadedCount === this.totalCount;
  }

  /**
   * Get loaded image by path
   */
  getLoadedImage(imagePath: string): HTMLImageElement | null {
    const index = this.imageList.indexOf(imagePath);
    return index > -1 ? this.loadedImages[index] : null;
  }

  /**
   * Get all loaded images
   */
  getAllLoadedImages(): HTMLImageElement[] {
    return [...this.loadedImages];
  }

  /**
   * Add progress callback
   */
  onProgress(callback: ProgressCallback): void {
    this.onProgressCallbacks.push(callback);
  }

  /**
   * Add completion callback
   */
  onComplete(callback: CompleteCallback): void {
    this.onCompleteCallbacks.push(callback);
  }

  /**
   * Add error callback
   */
  onError(callback: ErrorCallback): void {
    this.onErrorCallbacks.push(callback);
  }

  /**
   * Notify progress callbacks
   */
  private notifyProgress(): void {
    const progress = this.getProgress();
    this.onProgressCallbacks.forEach(callback => {
      try {
        callback(progress, this.loadedCount, this.totalCount);
      } catch (error) {
        console.warn('Error in progress callback:', error);
      }
    });
  }

  /**
   * Notify completion callbacks
   */
  private notifyComplete(): void {
    this.onCompleteCallbacks.forEach(callback => {
      try {
        callback(this.loadedImages);
      } catch (error) {
        console.warn('Error in complete callback:', error);
      }
    });
  }

  /**
   * Notify error callbacks
   */
  private notifyError(error: Error): void {
    this.onErrorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (callbackError) {
        console.warn('Error in error callback:', callbackError);
      }
    });
  }

  /**
   * Clear all callbacks
   */
  clearCallbacks(): void {
    this.onProgressCallbacks = [];
    this.onCompleteCallbacks = [];
    this.onErrorCallbacks = [];
  }

  /**
   * Reset the preloader
   */
  reset(): void {
    this.loadedImages = [];
    this.loadedCount = 0;
    this.clearCallbacks();
  }

  /**
   * Destroy the preloader instance
   */
  destroy(): void {
    this.reset();
    this.imageList = [];
    this.totalCount = 0;
  }
}
